import * as React from 'react';
import {
	ComboBoxCompat as ComboBox,
	IDropdownOption,
	mergeStyleSets,
	useTheme,
} from 'components/FluentUICompat';
import type {Theme} from 'components/FluentUICompat';
import {IComboBox, IComboBoxStyles} from '@fluentui/react';

export interface SearchableComboBoxProps {
	label: string;
	options: IDropdownOption[];
	selectedKey?: string | null;
	text: string;
	onInputValueChange: (text: string) => void;
	onChange: (event: React.FormEvent<any>, option?: IDropdownOption) => void;
	onClear: () => void;
	disabled?: boolean;
	calloutMaxHeight?: number;
	openOnFocus?: boolean;
	openOnHover?: boolean;
	hideChevron?: boolean;
}

const getStyles = (theme: Theme) =>
	mergeStyleSets({
		comboWrap: {position: 'relative'},
		clearBtn: {
			position: 'absolute',
			right: 32,
			bottom: 6,
			width: 16,
			height: 16,
			border: 'none',
			background: theme.palette.neutralLight,
			color: theme.palette.neutralSecondary,
			borderRadius: '50%',
			cursor: 'pointer',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			fontSize: 10,
			lineHeight: 1,
			padding: 0,
			':hover': {
				background: theme.palette.neutralQuaternary,
				color: theme.palette.neutralPrimary,
			},
		},
	});

export const SearchableComboBox: React.FC<SearchableComboBoxProps> = ({
	label,
	options,
	selectedKey,
	text,
	onInputValueChange,
	onChange,
	onClear,
	disabled,
	calloutMaxHeight = 200,
	openOnFocus = true,
	openOnHover = false,
	hideChevron = true,
}) => {
	const theme = useTheme();
	const classNames = React.useMemo(() => getStyles(theme), [theme]);

	const comboRef = React.useRef<IComboBox | null>(null);

	const openMenu = React.useCallback(() => {
		if (disabled) return;
		comboRef.current?.focus(true);
	}, [disabled]);

	const caretStyles = React.useMemo<Partial<IComboBoxStyles>>(
		() => ({
			caretDownWrapper: hideChevron ? {display: 'none'} : undefined,
			root: hideChevron ? {paddingRight: 0} : undefined,
		}),
		[hideChevron],
	);

	return (
		<div
			className={classNames.comboWrap}
			onMouseEnter={openOnHover ? openMenu : undefined}
		>
			<ComboBox
				componentRef={comboRef}
				label={label}
				allowFreeform
				calloutProps={{calloutMaxHeight}}
				options={options}
				selectedKey={selectedKey ?? null}
				text={text}
				disabled={disabled}
				styles={caretStyles}
				onFocus={openOnFocus ? openMenu : undefined}
				onInputValueChange={inputText => onInputValueChange(inputText ?? '')}
				onChange={onChange}
			/>

			{text && (
				<button
					className={classNames.clearBtn}
					style={hideChevron ? {right: 6} : undefined}
					onClick={onClear}
					aria-label={`Clear ${label}`}
					type='button'
				>
					&#x2715;
				</button>
			)}
		</div>
	);
};
